import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, Lock, Database, Share2, ShieldCheck, Cookie, Clock, UserCheck, RefreshCw, Mail, Globe, MapPin } from 'lucide-react';
import { SEO } from '../components/SEO';

function PrivacyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      num: '1', title: 'Information We Collect',
      intro: 'We collect information that you provide directly to us when making a booking:',
      items: [
        { label: 'Personal Identity Information', text: 'Name, phone number, and email address.' },
        { label: 'Booking Details', text: 'Date, time slot, and sport selected.' },
        { label: 'Payment Information', text: 'We do not store your credit card or bank details. All payments are processed through secure, third-party payment gateways (e.g., Cashfree, UPI providers).' },
      ]
    },
    {
      num: '2', title: 'How We Use Your Information',
      intro: 'We use the collected data for the following purposes:',
      bullets: [
        'To process and confirm your turf bookings.',
        'To send booking confirmations and reminders via SMS or WhatsApp.',
        'To contact you in case of rescheduling due to weather or technical issues.',
        'To improve our website functionality and customer service.',
      ]
    },
    {
      num: '3', title: 'Data Sharing and Disclosure',
      intro: 'We do not sell, trade, or rent your personal information to third parties. We may share your data only in the following limited circumstances:',
      items: [
        { label: 'Service Providers', text: 'With trusted third-party partners who help us operate our website and process payments (under strict confidentiality).' },
        { label: 'Legal Requirements', text: 'If required by law or government authorities to comply with legal processes.' },
      ]
    },
    {
      num: '4', title: 'Data Security',
      text: 'We implement a variety of security measures to maintain the safety of your personal information. Your data is stored on secure servers, and any sensitive payment information is encrypted via Secure Socket Layer (SSL) technology.'
    },
    {
      num: '5', title: 'Cookies',
      text: 'Our website may use "cookies" to enhance the user experience. Cookies help us understand how you use the site and remember your preferences for future visits. You can choose to disable cookies through your browser settings, though some features of the site may not function properly as a result.'
    },
    {
      num: '6', title: 'Data Retention',
      text: 'We retain your personal information only for as long as necessary to fulfill your bookings and for legal or accounting purposes.'
    },
    {
      num: '7', title: 'Your Rights',
      intro: 'You have the right to:',
      bullets: [
        'Request access to the personal data we hold about you.',
        'Request the correction of any inaccurate information.',
        'Request the deletion of your data (provided there are no pending legal or booking obligations).',
      ]
    },
    {
      num: '8', title: 'Changes to This Policy',
      text: 'Malda Turf reserves the right to update this Privacy Policy at any time. Any changes will be posted on this page with an updated "Last Updated" date.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Privacy Policy"
        description="Learn how Malda Turf collects, uses, and protects your personal information when you book turf slots online."
        path="/privacy"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#16A34A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50px' }}>
              <Trophy className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base font-bold tracking-tight font-heading">Malda Turf</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors font-body">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                <Lock className="h-5 w-5 text-[#16A34A]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading">Privacy Policy</h1>
                <p className="text-[11px] text-neutral-500 font-body mt-0.5">Last updated: April 2026</p>
              </div>
            </div>
            <p className="text-sm text-neutral-400 font-body leading-relaxed">
              Welcome to <a href="https://maldaturf.in" className="text-[#16A34A] hover:underline">Malda Turf</a>. We value your privacy and are committed to protecting your personal data. This Privacy Policy outlines how we collect, use, and safeguard your information when you use our website to book sports slots.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.num} className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid={`privacy-section-${section.num}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">{section.num}</span>
                  <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">{section.title}</h2>
                </div>
                <div className="pl-10">
                  {section.intro && <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">{section.intro}</p>}
                  {section.items && (
                    <div className="space-y-3">
                      {section.items.map((item, i) => (
                        <div key={i} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                          <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">{item.label}</div>
                          <p className="text-sm text-neutral-400 font-body leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {section.text && <p className="text-sm text-neutral-400 font-body leading-relaxed">{section.text}</p>}
                  {section.bullets && (
                    <ul className="space-y-2.5">
                      {section.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                          <span className="text-sm text-neutral-400 font-body leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid="privacy-section-9">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">9</span>
                <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">Contact Us</h2>
              </div>
              <div className="pl-10">
                <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">If you have any questions regarding this Privacy Policy, you may contact us at:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <a href="https://maldaturf.in" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                    <Globe className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Website</div>
                    <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">maldaturf.in</div>
                  </a>
                  <a href="mailto:maldaturf@gmail.com" className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors group">
                    <Mail className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Email</div>
                    <div className="text-[12px] text-neutral-300 font-body group-hover:text-white transition-colors">maldaturf@gmail.com</div>
                  </a>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                    <MapPin className="h-4 w-4 text-[#16A34A] mb-2" />
                    <div className="text-[10px] text-neutral-600 font-body mb-0.5">Address</div>
                    <div className="text-[12px] text-neutral-300 font-body">Malda, West Bengal</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-[11px] text-neutral-600 font-body">
              By using Malda Turf, you acknowledge and agree to the terms of this Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPage;
